import { useState, useEffect } from "react";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { resolveBackorderPhotoUrl } from "@/utils/backorderPhoto";
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  Package,
  Send,
  Building2,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Mail,
  Check,
  X,
  Save,
  ExternalLink,
  MessageSquare,
  Users,
  Truck,
  DollarSign,
  UserPlus,
  Settings,
  Camera,
  Image as ImageIcon,
  Calculator,
  FileText,
  Download,
  LayoutGrid,
  List
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import TicketChat from "@/components/TicketChat";
import { MarkupCalculatorModal } from "./MarkupCalculator";
import BackorderPipeline from "@/components/BackorderPipeline";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import QuoteCard, { QuoteCardList } from "@/components/QuoteCard";

// Compact Backorder Detail Panel - Slide-out style for quick viewing
const BackorderDetailPanel = ({ 
  request, 
  onClose, 
  onStatusChange, 
  onSendToSuppliers,
  onFinalizePricing,
  onApproveQuote,
  onDeclineQuote,
  onDownloadQuotePdf,
  onEmailQuotePdf,
  token 
}) => {
  const [supplierData, setSupplierData] = useState(null);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const statusConfig = BACKORDER_STATUS_CONFIG[request?.status] || BACKORDER_STATUS_CONFIG.submitted;

  // Fetch quotes for this request
  useEffect(() => {
    if (!request?.id || !token) return;
    
    const fetchQuotes = async () => {
      setLoadingQuotes(true);
      try {
        const res = await fetch(`${API}/backorders/requests/${request.id}/quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSupplierData(data);
        }
      } catch (err) {
        console.error("Failed to fetch quotes:", err);
      } finally {
        setLoadingQuotes(false);
      }
    };
    fetchQuotes();
  }, [request?.id, token]);

  // Add note to request
  const handleAddNote = async () => {
    if (!newNote.trim() || !request?.id) return;
    setAddingNote(true);
    try {
      await fetch(`${API}/backorders/requests/${request.id}/notes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: newNote })
      });
      setNewNote("");
      toast.success("Note added");
    } catch (err) {
      toast.error("Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  if (!request) return null;

  // Count quotes
  const totalQuotes = supplierData?.total_quotes || 0;
  const items = supplierData?.items || {};

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500 font-mono text-sm">#{request.id?.slice(0, 8).toUpperCase()}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
              {statusConfig.label}
            </span>
          </div>
          <h2 className="text-lg font-bold text-slate-900 mt-1">{request.hospital_name}</h2>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 hover:text-slate-900">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable Content */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-2">
            {request.status === "submitted" && (
              <Button size="sm" onClick={() => onSendToSuppliers(request.id)} className="bg-purple-600 hover:bg-purple-700 text-white">
                <Send className="w-3 h-3 mr-1" /> Send to Suppliers
              </Button>
            )}
            {(request.status === "sent_to_suppliers" || request.status === "quotes_received") && (
              <Button size="sm" onClick={() => onFinalizePricing(request.id)} className="bg-cyan-600 hover:bg-cyan-700 text-white">
                <DollarSign className="w-3 h-3 mr-1" /> Finalize
              </Button>
            )}
            <Select value={request.status} onValueChange={(v) => onStatusChange(request.id, v)}>
              <SelectTrigger className="w-40 h-8 bg-white border-slate-300 text-slate-900 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(BACKORDER_STATUS_CONFIG).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-100 rounded-lg p-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="text-slate-500 text-xs">Contact</span>
                <p className="text-slate-900">{request.contact_name}</p>
              </div>
              <div>
                <span className="text-slate-500 text-xs">Email</span>
                <p className="text-slate-900 truncate">{request.contact_email}</p>
              </div>
              {request.submitted_by && (
                <div className="col-span-2">
                  <span className="text-slate-500 text-xs">Requester</span>
                  <p className="text-slate-900">{request.submitted_by}</p>
                </div>
              )}
            </div>
          </div>

          {/* Request Items */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Items ({request.items?.length || 0})
            </h3>
            <div className="space-y-2">
              {request.items?.map((item, idx) => (
                <div key={idx} className="bg-slate-100 rounded-lg p-3 border border-slate-200">
                  <div className="flex items-start gap-3">
                    {item.photo_url && (
                      <a
                        href={resolveBackorderPhotoUrl(item.photo_url)}
                        target="_blank"
                        rel="noreferrer"
                        title="Open full-size photo in a new tab"
                        data-testid={`bo-item-photo-${idx}`}
                      >
                        <img
                          src={resolveBackorderPhotoUrl(item.photo_url)}
                          alt=""
                          className="w-12 h-12 rounded object-cover cursor-zoom-in hover:ring-2 hover:ring-slate-400"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </a>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{item.item_name}</p>
                      <p className="text-xs text-slate-600">{item.manufacturer} • {item.mfr_number}</p>
                      <p className="text-xs text-slate-500 mt-1">Qty: {item.quantity} {item.quantity_type}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Supplier Quotes Section */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Supplier Quotes ({totalQuotes})
            </h3>
            
            {loadingQuotes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
              </div>
            ) : totalQuotes === 0 ? (
              <div className="bg-slate-100 rounded-lg p-4 text-center text-slate-500 text-sm border border-slate-200">
                No quotes received yet
              </div>
            ) : (
              <div className="space-y-3">
                {/* Flatten all quotes from items and render with QuoteCard */}
                {Object.entries(items).flatMap(([itemId, itemData]) => 
                  (itemData.quotes || []).map((quote, qIdx) => (
                    <QuoteCard
                      key={`${itemId}-${qIdx}`}
                      quote={{
                        ...quote,
                        item_name: quote.item_name || itemData.item?.name,
                        manufacturer: quote.manufacturer || itemData.item?.manufacturer,
                        mfr_number: quote.mfr_number || itemData.item?.part_number,
                        quantity: quote.quantity || itemData.item?.quantity || 1
                      }}
                      request={request}
                      viewMode="admin"
                      onApprove={onApproveQuote}
                      onDecline={onDeclineQuote}
                      onRefresh={() => {
                        // Re-fetch quotes after action
                        const refetch = async () => {
                          try {
                            const res = await fetch(`${API}/backorders/requests/${request.id}/quotes`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setSupplierData(data);
                            }
                          } catch (err) {
                            console.error("Failed to refresh quotes:", err);
                          }
                        };
                        refetch();
                      }}
                      token={token}
                    />
                  ))
                )}
              </div>
            )}
          </div>

          {/* Notes Section */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Notes & Updates
            </h3>
            
            {/* Add Note */}
            <div className="flex gap-2 mb-3">
              <Input 
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                className="bg-white border-slate-300 text-slate-900 text-sm h-8"
              />
              <Button 
                size="sm" 
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="h-8 px-3"
              >
                {addingNote ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </Button>
            </div>

            {/* Notes List */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {Array.isArray(request.notes) && request.notes.length > 0 ? (
                request.notes.map((note, idx) => (
                  <div key={idx} className="bg-slate-100 rounded p-2 text-sm border border-slate-200">
                    <p className="text-slate-800">{typeof note === 'string' ? note : note.text}</p>
                    {note.created_at && (
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(note.created_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))
              ) : (request.notes_text || (typeof request.notes === 'string' && request.notes)) ? (
                <div className="bg-slate-100 rounded p-2 text-sm border border-slate-200">
                  <p className="text-slate-800">{request.notes_text || request.notes}</p>
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-2">No notes yet</p>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* Footer with Total */}
      {request.total_cost && (
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Estimated Total</span>
            <span className="text-2xl font-bold text-green-600">${request.total_cost.toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Status configuration for backorders
const BACKORDER_STATUS_CONFIG = {
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700 border-blue-200" },
  sent_to_suppliers: { label: "Sent to Suppliers", color: "bg-purple-100 text-purple-700 border-purple-200" },
  quotes_received: { label: "Quotes Received", color: "bg-amber-100 text-amber-700 border-amber-200" },
  priced: { label: "Priced", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  pending_approval: { label: "Pending Approval", color: "bg-orange-100 text-orange-700 border-orange-200" },
  approved: { label: "Approved", color: "bg-green-100 text-green-700 border-green-200" },
  ordered: { label: "Ordered", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  shipped: { label: "Shipped", color: "bg-teal-100 text-teal-700 border-teal-200" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700 border-red-200" }
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color, link }) => {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    green: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    purple: "bg-purple-50 text-purple-600 border-purple-100",
    red: "bg-red-50 text-red-600 border-red-100",
    slate: "bg-slate-50 text-slate-600 border-slate-100"
  };
  
  const Card = (
    <div className={`p-4 rounded-xl border ${colorClasses[color] || colorClasses.blue} ${link ? 'hover:shadow-md cursor-pointer transition-shadow' : ''}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color] || colorClasses.blue}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm opacity-75">{title}</p>
        </div>
      </div>
    </div>
  );
  
  return link ? <Link to={link}>{Card}</Link> : Card;
};

// Auto-send settings component
const AutoSendSettings = ({ token }) => {
  const [settings, setSettings] = useState({ enabled: false, email_template: "" });
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API}/backorders/settings/auto-send`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setSettings(data);
        }
      } catch (err) {
        console.error("Failed to fetch settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [token]);

  const handleToggle = async () => {
    if (toggling) return;
    setToggling(true);
    
    const newEnabled = !settings.enabled;
    
    // Optimistic update
    setSettings(prev => ({ ...prev, enabled: newEnabled }));
    
    try {
      const res = await fetch(`${API}/backorders/settings/auto-send`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...settings, enabled: newEnabled })
      });
      
      if (res.ok) {
        toast.success(newEnabled ? "Auto-send enabled" : "Auto-send disabled");
      } else {
        // Revert on failure
        setSettings(prev => ({ ...prev, enabled: !newEnabled }));
        toast.error("Failed to update settings");
      }
    } catch (err) {
      // Revert on error
      setSettings(prev => ({ ...prev, enabled: !newEnabled }));
      toast.error("Failed to update settings");
    } finally {
      setToggling(false);
    }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin" />;

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-slate-700">Auto-send to suppliers</span>
      <button
        onClick={handleToggle}
        disabled={toggling}
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          settings.enabled ? 'bg-blue-600' : 'bg-slate-300'
        } ${toggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        role="switch"
        aria-checked={settings.enabled}
        aria-label="Auto-send to suppliers"
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200 ${
            settings.enabled ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
      <span className={`text-xs font-medium ${settings.enabled ? 'text-green-600' : 'text-slate-400'}`}>
        {settings.enabled ? 'ON' : 'OFF'}
      </span>
    </div>
  );
};

// Supplier Status Section Component - Shows all supplier responses for a request
const SupplierStatusSection = ({ requestId, token, onRefresh, request }) => {
  const [supplierData, setSupplierData] = useState(null);
  const [allQuotes, setAllQuotes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("status"); // "status" or "quotes"
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [processing, setProcessing] = useState(false);
  
  // Markup Calculator state
  const [showMarkupCalculator, setShowMarkupCalculator] = useState(false);
  const [selectedQuoteForMarkup, setSelectedQuoteForMarkup] = useState(null);

  useEffect(() => {
    const fetchSupplierStatus = async () => {
      try {
        const [statusRes, quotesRes] = await Promise.all([
          fetch(`${API}/backorders/requests/${requestId}/supplier-status`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API}/backorders/requests/${requestId}/quotes`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        if (statusRes.ok) {
          const data = await statusRes.json();
          setSupplierData(data);
        }
        if (quotesRes.ok) {
          const data = await quotesRes.json();
          setAllQuotes(data);
        }
      } catch (err) {
        console.error("Failed to fetch supplier data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSupplierStatus();
  }, [requestId, token]);

  const handleQuoteAction = async (quoteId, action) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/backorders/quotes/${quoteId}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, notes: null })
      });
      if (res.ok) {
        toast.success(`Quote ${action}ed successfully`);
        // Refresh data
        onRefresh();
      } else {
        throw new Error("Failed to process quote");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Download quote as PDF
  const handleDownloadQuotePdf = async (quote, itemData, backorderRequest) => {
    try {
      const quoteData = {
        customer_name: backorderRequest?.hospital_name || "Customer",
        customer_email: "",
        customer_address: "",
        items: [{
          item_number: itemData?.item?.manufacturer_number || "",
          description: quote.item_name || itemData?.item?.item_name || "",
          quantity: quote.quantity_available || itemData?.item?.quantity || 1,
          unit_price: quote.customer_price || 0,
          total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1)
        }],
        subtotal: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        discount_total: 0,
        total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        notes: quote.supplier_notes || "",
        valid_days: 30
      };

      const res = await fetch(`${API}/marketplace/quote/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(quoteData)
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Quote_${quote.id?.slice(0, 8) || 'quote'}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Quote PDF downloaded");
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download quote PDF");
    }
  };

  // Email quote PDF
  const handleEmailQuotePdf = async (quote, itemData, backorderRequest) => {
    const email = prompt("Enter email address to send quote:", backorderRequest?.contact_email || "");
    if (!email || !email.includes("@")) {
      if (email !== null) toast.error("Please enter a valid email address");
      return;
    }

    try {
      const quoteData = {
        customer_name: backorderRequest?.hospital_name || "Customer",
        customer_email: email,
        customer_address: "",
        items: [{
          item_number: itemData?.item?.manufacturer_number || "",
          description: quote.item_name || itemData?.item?.item_name || "",
          quantity: quote.quantity_available || itemData?.item?.quantity || 1,
          unit_price: quote.customer_price || 0,
          total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1)
        }],
        subtotal: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        discount_total: 0,
        total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        notes: quote.supplier_notes || "",
        valid_days: 30
      };

      toast.loading("Sending quote PDF...");
      
      const res = await fetch(`${API}/marketplace/quote/email?email_to=${encodeURIComponent(email)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(quoteData)
      });

      const data = await res.json();
      toast.dismiss();
      
      if (data.status === "success") {
        toast.success(`Quote sent to ${email}`);
      } else {
        toast.error(data.message || "Failed to send quote");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Email error:", error);
      toast.error("Failed to email quote PDF");
    }
  };

  const handleSelectQuotes = async () => {
    if (selectedQuotes.length === 0) {
      toast.error("Please select at least one quote");
      return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/backorders/requests/${requestId}/select-quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(selectedQuotes)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Selected ${selectedQuotes.length} quotes - Total: $${data.total_order_value?.toLocaleString()}`);
        setSelectedQuotes([]);
        onRefresh();
      } else {
        throw new Error("Failed to select quotes");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const toggleQuoteSelection = (quoteId) => {
    setSelectedQuotes(prev => 
      prev.includes(quoteId) 
        ? prev.filter(id => id !== quoteId)
        : [...prev, quoteId]
    );
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="ml-2 text-slate-500">Loading supplier responses...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900 font-manrope flex items-center gap-2">
          <Users className="w-5 h-5 text-purple-600" />
          Supplier Responses
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveView("status")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeView === "status" ? "bg-purple-100 text-purple-700" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            Status ({supplierData?.total_suppliers_notified || 0})
          </button>
          <button
            onClick={() => setActiveView("quotes")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeView === "quotes" ? "bg-green-100 text-green-700" : "text-slate-500 hover:bg-slate-100"
            }`}
          >
            Quotes ({allQuotes?.total_quotes || 0})
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-slate-800">{supplierData?.total_suppliers_notified || 0}</p>
          <p className="text-xs text-slate-500">Suppliers Notified</p>
        </div>
        <div className="bg-emerald-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{supplierData?.suppliers_acknowledged || 0}</p>
          <p className="text-xs text-slate-500">Acknowledged</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">{supplierData?.suppliers_quoted || 0}</p>
          <p className="text-xs text-slate-500">Quotes Received</p>
        </div>
      </div>

      {activeView === "status" && (
        <div className="space-y-3">
          {supplierData?.suppliers?.map((supplier) => (
            <div key={supplier.supplier_id} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-slate-800">{supplier.supplier_name}</h4>
                  <p className="text-slate-500 text-sm">{supplier.supplier_email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {supplier.acknowledged ? (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Acknowledged
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Pending
                    </span>
                  )}
                  {supplier.quotes_submitted && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {supplier.quotes_count} Quote(s)
                    </span>
                  )}
                </div>
              </div>
              {supplier.response_status && supplier.response_status !== "quoted" && (
                <div className="mt-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    supplier.response_status === "not_in_stock" ? "bg-red-100 text-red-700" :
                    supplier.response_status === "need_more_info" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>
                    {supplier.response_status.replace(/_/g, " ").toUpperCase()}
                  </span>
                </div>
              )}
              {supplier.acknowledgement_notes && (
                <p className="mt-2 text-sm text-slate-600 italic">&ldquo;{supplier.acknowledgement_notes}&rdquo;</p>
              )}
              {supplier.total_quoted > 0 && (
                <p className="mt-2 text-sm text-green-600 font-medium">
                  Total Quoted: ${supplier.total_quoted.toLocaleString()}
                </p>
              )}
            </div>
          ))}
          {(!supplierData?.suppliers || supplierData.suppliers.length === 0) && (
            <p className="text-center text-slate-400 py-4">No suppliers notified yet</p>
          )}
        </div>
      )}

      {activeView === "quotes" && (
        <div className="space-y-4">
          {selectedQuotes.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <span className="text-blue-700 font-medium">{selectedQuotes.length} quote(s) selected</span>
              <Button
                size="sm"
                onClick={handleSelectQuotes}
                disabled={processing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                Confirm Selection
              </Button>
            </div>
          )}
          
          {allQuotes?.items && Object.entries(allQuotes.items).map(([itemId, itemData]) => (
            <div key={itemId} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-slate-800">{itemData.item?.item_name || "Item"}</h4>
                  <p className="text-slate-500 text-sm">
                    Requested: {itemData.item?.quantity} {itemData.item?.quantity_type || "units"}
                    {itemData.item?.mfr_number && ` • MFR: ${itemData.item.mfr_number}`}
                  </p>
                </div>
                {itemData.lowest_quote && (
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Lowest Quote</p>
                    <p className="text-lg font-bold text-green-600">${itemData.lowest_quote.toFixed(2)}</p>
                  </div>
                )}
              </div>
              
              {itemData.quotes?.length > 0 ? (
                <div className="space-y-2">
                  {itemData.quotes.map((quote) => (
                    <div 
                      key={quote.id} 
                      className={`bg-white rounded-lg p-3 border transition-colors ${
                        selectedQuotes.includes(quote.id) 
                          ? "border-blue-400 bg-blue-50" 
                          : quote.status === "approved" 
                          ? "border-green-300 bg-green-50" 
                          : "border-slate-200"
                      }`}
                    >
                      <div className="flex items-start justify-between p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
                        <div className="flex items-start gap-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedQuotes.includes(quote.id)}
                            onChange={() => toggleQuoteSelection(quote.id)}
                            className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            disabled={quote.status === "approved"}
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-slate-900">{quote.supplier_name}</p>
                              {quote.original_or_substitute && (
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  quote.original_or_substitute === "original" 
                                    ? "bg-green-100 text-green-700" 
                                    : "bg-amber-100 text-amber-700"
                                }`}>
                                  {quote.original_or_substitute.toUpperCase()}
                                </span>
                              )}
                              {quote.status && (
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  quote.status === "approved" ? "bg-green-100 text-green-700" :
                                  quote.status === "rejected" ? "bg-red-100 text-red-700" :
                                  "bg-blue-100 text-blue-600"
                                }`}>
                                  {quote.status === "pending_review" ? "PENDING REVIEW" : quote.status?.toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-600 grid grid-cols-2 gap-x-4 gap-y-1 mt-2">
                              {quote.manufacturer_number && <p><span className="text-slate-400">MFR#:</span> {quote.manufacturer_number}</p>}
                              <p><span className="text-slate-400">Qty Available:</span> {quote.quantity_available || quote.available_quantity}</p>
                              {quote.eta_days && <p><span className="text-slate-400">Lead Time:</span> {quote.eta_days} days</p>}
                              {quote.freight_included !== undefined && (
                                <p><span className="text-slate-400">Freight:</span> {quote.freight_included ? "✓ Included" : `$${quote.freight_cost?.toFixed(2) || "N/A"}`}</p>
                              )}
                              {quote.valid_until && <p><span className="text-slate-400">Valid Until:</span> {quote.valid_until}</p>}
                            </div>
                            {quote.supplier_notes && (
                              <p className="text-sm text-slate-500 italic mt-2 bg-slate-50 p-2 rounded">&ldquo;{quote.supplier_notes}&rdquo;</p>
                            )}
                          </div>
                        </div>
                        
                        {/* DUAL PRICING DISPLAY - Financial Veil */}
                        <div className="text-right min-w-[220px] ml-4">
                          {/* Supplier Cost Box */}
                          <div className="bg-slate-100 rounded-lg p-3 mb-2">
                            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Your Cost (Supplier)</p>
                            <p className="text-slate-700 font-mono text-lg">${(quote.supplier_cost || quote.unit_price)?.toFixed(2)}/unit</p>
                            <p className="text-slate-600 text-sm font-medium mt-1">
                              Total: ${((quote.supplier_cost || quote.unit_price || 0) * (quote.quantity_available || itemData.item?.quantity || 1)).toFixed(2)}
                            </p>
                          </div>
                          
                          {/* Hospital Price Box */}
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
                            <p className="text-xs text-green-700 uppercase tracking-wide mb-1">Hospital Price</p>
                            <p className="text-green-700 font-bold text-xl">${quote.customer_price?.toFixed(2)}/unit</p>
                            <p className="text-xs text-green-600">(+{quote.markup_percentage || 35}% markup)</p>
                            <p className="text-green-800 font-semibold mt-1">
                              Total: ${((quote.customer_price || 0) * (quote.quantity_available || itemData.item?.quantity || 1)).toFixed(2)}
                            </p>
                          </div>
                          
                          {/* PROFIT MARGIN BOX - New */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-700 uppercase tracking-wide mb-1">Your Profit</p>
                            {(() => {
                              const qty = quote.quantity_available || itemData.item?.quantity || 1;
                              const cost = (quote.supplier_cost || quote.unit_price || 0) * qty;
                              const revenue = (quote.customer_price || 0) * qty;
                              const profit = revenue - cost;
                              const marginPct = cost > 0 ? ((profit / cost) * 100).toFixed(0) : 0;
                              return (
                                <>
                                  <p className="text-blue-800 font-bold text-lg">${profit.toFixed(2)}</p>
                                  <p className="text-xs text-blue-600">{marginPct}% margin on {qty} units</p>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                      {/* PDF Download/Email buttons - Always visible */}
                      <div className="flex justify-start gap-2 mt-3 pt-3 border-t border-slate-100">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadQuotePdf(quote, itemData, request)}
                          className="border-slate-200 text-slate-600 hover:bg-slate-50"
                          data-testid={`download-quote-pdf-${quote.id}`}
                        >
                          <Download className="w-3 h-3 mr-1" /> Download PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEmailQuotePdf(quote, itemData, request)}
                          className="border-slate-200 text-slate-600 hover:bg-slate-50"
                          data-testid={`email-quote-pdf-${quote.id}`}
                        >
                          <Mail className="w-3 h-3 mr-1" /> Email Quote
                        </Button>
                      </div>
                      {quote.status !== "approved" && (
                        <div className="flex justify-end gap-2 mt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleQuoteAction(quote.id, "reject")}
                            disabled={processing}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <X className="w-3 h-3 mr-1" /> Decline
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedQuoteForMarkup({
                                ...quote,
                                itemName: itemData.item?.item_name
                              });
                              setShowMarkupCalculator(true);
                            }}
                            disabled={processing}
                            className="border-blue-200 text-blue-600 hover:bg-blue-50"
                            data-testid={`process-markup-${quote.id}`}
                          >
                            <Calculator className="w-3 h-3 mr-1" /> Edit Markup
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleQuoteAction(quote.id, "approve")}
                            disabled={processing}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Check className="w-3 h-3 mr-1" /> Approve & Send to Hospital
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm text-center py-2">No quotes received for this item</p>
              )}
            </div>
          ))}
          {(!allQuotes?.items || Object.keys(allQuotes.items).length === 0) && (
            <p className="text-center text-slate-400 py-4">No quotes received yet</p>
          )}
        </div>
      )}
      
      {/* Markup Calculator Modal */}
      <MarkupCalculatorModal
        isOpen={showMarkupCalculator}
        onClose={() => {
          setShowMarkupCalculator(false);
          setSelectedQuoteForMarkup(null);
        }}
        ticketType="backorder"
        ticketId={requestId}
        supplierQuote={selectedQuoteForMarkup}
        supplierName={selectedQuoteForMarkup?.supplier_name}
        itemName={selectedQuoteForMarkup?.itemName}
        onQuoteProcessed={() => {
          onRefresh?.();
          setShowMarkupCalculator(false);
          setSelectedQuoteForMarkup(null);
        }}
      />
    </div>
  );
};

export default function BackordersManagement() {
  const [requests, setRequests] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list"); // "list" or "pipeline"
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showHospitalModal, setShowHospitalModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ name: "", email: "", contact_name: "", phone: "" });
  const [newHospital, setNewHospital] = useState({ hospital_name: "", contact_name: "", email: "", phone: "", address: "" });
  const [newQuote, setNewQuote] = useState({ supplier_id: "", unit_price: "", available_quantity: "", lead_time_days: "", notes: "" });
  const [newItem, setNewItem] = useState({ item_name: "", manufacturer: "", mfr_number: "", quantity: "", quantity_type: "units", notes: "", photo_url: "" });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [activeTab, setActiveTab] = useState("requests");
  const [feedbackMessages, setFeedbackMessages] = useState([]);
  const [showProxyBackorderModal, setShowProxyBackorderModal] = useState(false);
  const [proxyBackorderData, setProxyBackorderData] = useState({
    hospital_id: "",
    health_system_name: "",
    urgency: "medium",
    notes: "",
    items: [{ item_name: "", mfr_number: "", quantity: 1, quantity_type: "units", notes: "" }]
  });
  const [creatingProxyBackorder, setCreatingProxyBackorder] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState({ tracking_number: "", carrier: "" });
  const { token, user } = useAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reqRes, suppRes, hospRes, regRes, feedbackRes] = await Promise.all([
        fetch(`${API}/backorders/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/backorders/suppliers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/backorders/hospitals`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/hospitals/register/requests`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/admin/feedback`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (reqRes.ok) {
        const data = await reqRes.json();
        setRequests(data.requests || []);
      }
      if (suppRes.ok) {
        const data = await suppRes.json();
        setSuppliers(data.suppliers || []);
      }
      if (feedbackRes.ok) {
        const data = await feedbackRes.json();
        setFeedbackMessages(data.feedback || []);
      }
      if (hospRes.ok) {
        const data = await hospRes.json();
        setHospitals(data.hospitals || []);
      }
      if (regRes.ok) {
        const data = await regRes.json();
        setRegistrationRequests(data.requests || []);
      }
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSendToSuppliers = async (requestId) => {
    try {
      const res = await fetch(`${API}/backorders/requests/${requestId}/send-to-suppliers`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to send");
      const data = await res.json();
      toast.success(`Sent to ${data.sent_to} suppliers`);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateStatus = async (requestId, newStatus, tracking = null) => {
    // If changing to "shipped" and no tracking provided, show modal
    if (newStatus === "shipped" && !tracking) {
      setPendingStatusChange({ requestId, newStatus });
      setTrackingInfo({ tracking_number: "", carrier: "" });
      setShowTrackingModal(true);
      return;
    }
    
    try {
      const payload = { 
        status: newStatus,
        send_notifications: true
      };
      
      // Add tracking info if provided
      if (tracking) {
        payload.tracking_number = tracking.tracking_number;
        payload.carrier = tracking.carrier;
      }
      
      const res = await fetch(`${API}/backorders/${requestId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error("Failed to update");
      toast.success(newStatus === "shipped" && tracking ? "Shipped with tracking info!" : "Status updated");
      fetchData();
      if (selectedRequest?.id === requestId) {
        setSelectedRequest({ ...selectedRequest, status: newStatus, ...tracking });
      }
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  // Handle tracking modal submit
  const handleTrackingSubmit = () => {
    if (!trackingInfo.tracking_number) {
      toast.error("Please enter a tracking number");
      return;
    }
    if (!trackingInfo.carrier) {
      toast.error("Please select a carrier");
      return;
    }
    
    handleUpdateStatus(pendingStatusChange.requestId, pendingStatusChange.newStatus, trackingInfo);
    setShowTrackingModal(false);
    setPendingStatusChange(null);
  };

  const handleAddQuote = async () => {
    if (!selectedItem || !newQuote.supplier_id || !newQuote.unit_price) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      const res = await fetch(`${API}/backorders/requests/${selectedRequest.id}/items/${selectedItem.id}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          backorder_id: selectedRequest.id,
          item_id: selectedItem.id,
          supplier_id: newQuote.supplier_id,
          unit_price: parseFloat(newQuote.unit_price),
          available_quantity: parseInt(newQuote.available_quantity) || 0,
          lead_time_days: parseInt(newQuote.lead_time_days) || null,
          notes: newQuote.notes
        })
      });
      if (!res.ok) throw new Error("Failed to add quote");
      toast.success("Quote added with 35% markup");
      setShowQuoteModal(false);
      setNewQuote({ supplier_id: "", unit_price: "", available_quantity: "", lead_time_days: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFinalizePricing = async (requestId) => {
    try {
      const res = await fetch(`${API}/backorders/requests/${requestId}/finalize-pricing`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to finalize");
      const data = await res.json();
      toast.success(`Pricing finalized - Total: $${data.total_cost.toLocaleString()}`);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Download quote as PDF
  const handleDownloadQuotePdf = async (quote, itemData, backorderRequest) => {
    try {
      const quoteData = {
        customer_name: backorderRequest?.hospital_name || "Customer",
        customer_email: "",
        customer_address: "",
        items: [{
          item_number: itemData?.item?.manufacturer_number || quote.item_name || "",
          description: quote.item_name || itemData?.item?.item_name || "",
          quantity: quote.quantity_available || itemData?.item?.quantity || 1,
          unit_price: quote.customer_price || 0,
          total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1)
        }],
        subtotal: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        discount_total: 0,
        total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        notes: quote.supplier_notes || "",
        valid_days: 30
      };

      const res = await fetch(`${API}/marketplace/quote/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(quoteData)
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Quote_${quote.id?.slice(0, 8) || 'quote'}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Quote PDF downloaded");
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download quote PDF");
    }
  };

  // Email quote PDF
  const handleEmailQuotePdf = async (quote, itemData, backorderRequest) => {
    const email = prompt("Enter email address to send quote:", backorderRequest?.contact_email || "");
    if (!email || !email.includes("@")) {
      if (email !== null) toast.error("Please enter a valid email address");
      return;
    }

    try {
      const quoteData = {
        customer_name: backorderRequest?.hospital_name || "Customer",
        customer_email: email,
        customer_address: "",
        items: [{
          item_number: itemData?.item?.manufacturer_number || quote.item_name || "",
          description: quote.item_name || itemData?.item?.item_name || "",
          quantity: quote.quantity_available || itemData?.item?.quantity || 1,
          unit_price: quote.customer_price || 0,
          total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1)
        }],
        subtotal: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        discount_total: 0,
        total: (quote.customer_price || 0) * (quote.quantity_available || itemData?.item?.quantity || 1),
        notes: quote.supplier_notes || "",
        valid_days: 30
      };

      toast.loading("Sending quote PDF...");
      const res = await fetch(`${API}/marketplace/quote/email?email_to=${encodeURIComponent(email)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(quoteData)
      });

      const data = await res.json();
      toast.dismiss();
      
      if (data.status === "success") {
        toast.success(`Quote sent to ${email}`);
      } else {
        toast.error(data.message || "Failed to send quote");
      }
    } catch (error) {
      toast.dismiss();
      console.error("Email error:", error);
      toast.error("Failed to email quote PDF");
    }
  };

  // Quote approve/decline actions
  const handleApproveQuote = async (quoteId) => {
    try {
      const res = await fetch(`${API}/backorders/quotes/${quoteId}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "approve" })
      });
      if (res.ok) {
        toast.success("Quote approved");
        fetchData();
      } else throw new Error("Failed to approve quote");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeclineQuote = async (quoteId) => {
    try {
      const res = await fetch(`${API}/backorders/quotes/${quoteId}/action`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: "reject" })
      });
      if (res.ok) {
        toast.success("Quote declined");
        fetchData();
      } else throw new Error("Failed to decline quote");
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Edit Item
  const handleEditItem = async () => {
    if (!editingItem) return;
    try {
      const res = await fetch(`${API}/backorders/requests/${selectedRequest.id}/items/${editingItem.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          item_name: editingItem.item_name,
          mfr_number: editingItem.mfr_number,
          quantity: parseInt(editingItem.quantity),
          quantity_type: editingItem.quantity_type,
          notes: editingItem.notes,
          status: editingItem.status,
          final_price: editingItem.final_price ? parseFloat(editingItem.final_price) : null,
          photo_url: editingItem.photo_url || null
        })
      });
      if (!res.ok) throw new Error("Failed to update item");
      toast.success("Item updated");
      setShowEditItemModal(false);
      setEditingItem(null);
      // Refresh the selected request
      const reqRes = await fetch(`${API}/backorders/requests/${selectedRequest.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (reqRes.ok) {
        const data = await reqRes.json();
        setSelectedRequest(data.request);
      }
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Delete Item
  const handleDeleteItem = async (itemId, itemName) => {
    if (!confirm(`Delete "${itemName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/backorders/requests/${selectedRequest.id}/items/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to delete item");
      toast.success("Item deleted");
      // Refresh the selected request
      const reqRes = await fetch(`${API}/backorders/requests/${selectedRequest.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (reqRes.ok) {
        const data = await reqRes.json();
        setSelectedRequest(data.request);
      }
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Photo Upload for Backorder Items
  const handlePhotoUpload = async (e, isEditMode = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Max 10MB.");
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API}/backorders/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (data.url) {
        if (isEditMode) {
          setEditingItem(prev => ({ ...prev, photo_url: data.url }));
        } else {
          setNewItem(prev => ({ ...prev, photo_url: data.url }));
        }
        toast.success("Photo uploaded");
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Add Item
  const handleAddItem = async () => {
    if (!newItem.item_name || !newItem.quantity) {
      toast.error("Item name and quantity required");
      return;
    }
    try {
      const res = await fetch(`${API}/backorders/requests/${selectedRequest.id}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          item_name: newItem.item_name,
          manufacturer: newItem.manufacturer,
          mfr_number: newItem.mfr_number,
          quantity: parseInt(newItem.quantity),
          quantity_type: newItem.quantity_type || "units",
          notes: newItem.notes,
          photo_url: newItem.photo_url || null
        })
      });
      if (!res.ok) throw new Error("Failed to add item");
      toast.success("Item added");
      setShowAddItemModal(false);
      setNewItem({ item_name: "", manufacturer: "", mfr_number: "", quantity: "", quantity_type: "units", notes: "", photo_url: "" });
      // Refresh
      const reqRes = await fetch(`${API}/backorders/requests/${selectedRequest.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (reqRes.ok) {
        const data = await reqRes.json();
        setSelectedRequest(data.request);
      }
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Edit Request Details
  const handleEditRequest = async (updates) => {
    try {
      const res = await fetch(`${API}/backorders/requests/${selectedRequest.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      if (!res.ok) throw new Error("Failed to update request");
      toast.success("Request updated");
      setShowEditRequestModal(false);
      // Refresh
      const reqRes = await fetch(`${API}/backorders/requests/${selectedRequest.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (reqRes.ok) {
        const data = await reqRes.json();
        setSelectedRequest(data.request);
      }
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Delete Request
  const handleDeleteRequest = async (requestId) => {
    if (!confirm("Delete this entire request? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/backorders/requests/${requestId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to delete request");
      toast.success("Request deleted");
      setSelectedRequest(null);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Add Note to Backorder Request
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedRequest) return;
    
    setSavingNote(true);
    try {
      const res = await fetch(`${API}/backorders/requests/${selectedRequest.id}/notes`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ note: newNote })
      });
      
      if (res.ok) {
        toast.success("Note saved");
        setNewNote("");
        // Refresh selected request to show new note
        const reqRes = await fetch(`${API}/backorders/requests/${selectedRequest.id}`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (reqRes.ok) {
          const data = await reqRes.json();
          setSelectedRequest(data.request);
        }
      } else {
        throw new Error("Failed to save note");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingNote(false);
    }
  };

  // Delete Quote
  const handleDeleteQuote = async (quoteId) => {
    if (!confirm("Delete this quote?")) return;
    try {
      const res = await fetch(`${API}/backorders/quotes/${quoteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to delete quote");
      toast.success("Quote deleted");
      // Refresh
      const reqRes = await fetch(`${API}/backorders/requests/${selectedRequest.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (reqRes.ok) {
        const data = await reqRes.json();
        setSelectedRequest(data.request);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Admin Proxy Backorder - Create backorder on behalf of hospital
  const handleCreateProxyBackorder = async () => {
    if (!proxyBackorderData.hospital_id) {
      toast.error("Please select a hospital");
      return;
    }
    if (!proxyBackorderData.items[0].item_name) {
      toast.error("Please add at least one item");
      return;
    }
    
    setCreatingProxyBackorder(true);
    try {
      const res = await fetch(`${API}/admin/backorder-proxy`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json", 
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({
          ...proxyBackorderData,
          created_by_admin: user?.email || "admin",
          admin_name: user?.name || user?.email || "Administrator"
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to create backorder");
      }
      
      const data = await res.json();
      toast.success(`Backorder ${data.request_id} created on behalf of ${proxyBackorderData.health_system_name}`);
      setShowProxyBackorderModal(false);
      setProxyBackorderData({
        hospital_id: "",
        health_system_name: "",
        urgency: "medium",
        notes: "",
        items: [{ item_name: "", mfr_number: "", quantity: 1, quantity_type: "units", notes: "" }]
      });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingProxyBackorder(false);
    }
  };

  // Add item to proxy backorder
  const addProxyItem = () => {
    setProxyBackorderData(prev => ({
      ...prev,
      items: [...prev.items, { item_name: "", mfr_number: "", quantity: 1, quantity_type: "units", notes: "" }]
    }));
  };

  // Remove item from proxy backorder
  const removeProxyItem = (index) => {
    if (proxyBackorderData.items.length <= 1) return;
    setProxyBackorderData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Update proxy item
  const updateProxyItem = (index, field, value) => {
    setProxyBackorderData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const handleCreateSupplier = async () => {
    if (!newSupplier.name || !newSupplier.email) {
      toast.error("Name and email required");
      return;
    }
    try {
      const res = await fetch(`${API}/backorders/suppliers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newSupplier)
      });
      if (!res.ok) throw new Error("Failed to create supplier");
      toast.success("Supplier created");
      setShowSupplierModal(false);
      setNewSupplier({ name: "", email: "", contact_name: "", phone: "" });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreateHospital = async () => {
    if (!newHospital.hospital_name || !newHospital.email) {
      toast.error("Hospital name and email required");
      return;
    }
    try {
      const res = await fetch(`${API}/backorders/hospitals`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newHospital)
      });
      if (!res.ok) throw new Error("Failed to create hospital");
      const data = await res.json();
      toast.success(`Hospital created. Access code: ${data.access_code}`);
      setShowHospitalModal(false);
      setNewHospital({ hospital_name: "", contact_name: "", email: "", phone: "", address: "" });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleApproveRegistration = async (requestId) => {
    try {
      const res = await fetch(`${API}/hospitals/register/approve/${requestId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to approve");
      }
      const data = await res.json();
      toast.success(`Approved! Access code sent to email.`);
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRejectRegistration = async (requestId, reason = "") => {
    try {
      const res = await fetch(`${API}/hospitals/register/reject/${requestId}?reason=${encodeURIComponent(reason)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to reject");
      toast.success("Registration request rejected");
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredRequests = statusFilter === "all" 
    ? requests 
    : requests.filter(r => r.status === statusFilter);

  const pendingRegistrations = registrationRequests.filter(r => r.status === "pending");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }
  // List View
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-manrope">Backorder Management</h2>
          <p className="text-slate-500 text-sm">{requests.length} requests • {hospitals.length} hospitals • {pendingRegistrations.length} pending registrations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="default"
            onClick={() => setShowProxyBackorderModal(true)} 
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="create-proxy-backorder-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Backorder for Hospital
          </Button>
          <Button variant="outline" onClick={() => setShowHospitalModal(true)} className="border-slate-200 text-slate-600 hover:bg-slate-50">
            <Building2 className="w-4 h-4 mr-2" /> Add Hospital
          </Button>
          <Button variant="outline" onClick={() => setShowSupplierModal(true)} className="border-slate-200 text-slate-600 hover:bg-slate-50">
            <Plus className="w-4 h-4 mr-2" /> Add Supplier
          </Button>
          <Button variant="outline" onClick={fetchData} className="border-slate-200 text-slate-600 hover:bg-slate-50">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab("requests")}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeTab === "requests"
              ? "bg-blue-700 text-white"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          }`}
        >
          <Package className="w-4 h-4 inline mr-2" />
          Requests ({requests.length})
        </button>
        <button
          onClick={() => setActiveTab("registrations")}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors relative ${
            activeTab === "registrations"
              ? "bg-green-600 text-white"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          }`}
        >
          <UserPlus className="w-4 h-4 inline mr-2" />
          Registrations
          {pendingRegistrations.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full">
              {pendingRegistrations.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab("hospitals")}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeTab === "hospitals"
              ? "bg-purple-600 text-white"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          }`}
        >
          <Building2 className="w-4 h-4 inline mr-2" />
          Hospitals ({hospitals.length})
        </button>
        <button
          onClick={() => setActiveTab("settings")}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
            activeTab === "settings"
              ? "bg-slate-700 text-white"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Auto-Send Settings
        </button>
        <button
          onClick={() => setActiveTab("feedback")}
          className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors relative ${
            activeTab === "feedback"
              ? "bg-amber-600 text-white"
              : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
          }`}
        >
          <MessageSquare className="w-4 h-4 inline mr-2" />
          Feedback
          {feedbackMessages.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
              {feedbackMessages.length}
            </span>
          )}
        </button>
      </div>

      {/* Feedback Tab */}
      {activeTab === "feedback" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">User Feedback & Messages</h3>
            <p className="text-sm text-slate-500">Messages sent to info@sterilemate.com</p>
          </div>
          {feedbackMessages.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No feedback messages yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {feedbackMessages.map((msg) => (
                <div key={msg.id} className={`p-4 rounded-lg border ${
                  msg.type === 'bug' ? 'bg-red-50 border-red-200' :
                  msg.type === 'feature' ? 'bg-amber-50 border-amber-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          msg.type === 'bug' ? 'bg-red-100 text-red-700' :
                          msg.type === 'feature' ? 'bg-amber-100 text-amber-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {msg.type === 'bug' ? 'Bug Report' : msg.type === 'feature' ? 'Feature Request' : 'Feedback'}
                        </span>
                        <span className="text-xs text-slate-500">{new Date(msg.submitted_at || msg.created_at).toLocaleString()}</span>
                      </div>
                      <h4 className="font-semibold text-slate-800">{msg.subject}</h4>
                      <p className="text-sm text-slate-600 mt-1">{msg.message}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-4 text-sm text-slate-500">
                    <span><strong>From:</strong> {msg.name}</span>
                    <span><strong>Email:</strong> <a href={`mailto:${msg.email}`} className="text-blue-600 hover:underline">{msg.email}</a></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab - Auto-Send Thresholds */}
      {activeTab === "settings" && (
        <AutoSendSettings token={token} />
      )}

      {/* Registration Requests Tab */}
      {activeTab === "registrations" && (
        <div className="space-y-4">
          <div className="card-clinical p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Pending Registration Requests</h3>
            {pendingRegistrations.length === 0 ? (
              <p className="text-slate-400 text-center py-8">No pending registration requests</p>
            ) : (
              <div className="space-y-4">
                {pendingRegistrations.map((req) => (
                  <div key={req.id} className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-white">{req.hospital_name}</h4>
                        <p className="text-slate-400 text-sm">{req.contact_name} • {req.email}</p>
                        <p className="text-slate-500 text-sm">{req.phone}</p>
                        {req.address && <p className="text-slate-500 text-sm">{req.address}</p>}
                        {req.reason && (
                          <p className="text-slate-300 text-sm mt-2 italic">&ldquo;{req.reason}&rdquo;</p>
                        )}
                        <p className="text-slate-500 text-xs mt-2">
                          Submitted {new Date(req.created_at).toLocaleDateString()} at {new Date(req.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-red-400 border-red-400/30 hover:bg-red-500/10"
                          onClick={() => handleRejectRegistration(req.id)}
                          data-testid={`reject-reg-${req.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApproveRegistration(req.id)}
                          data-testid={`approve-reg-${req.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* All Registration History */}
          <div className="card-clinical p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Registration History</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-3 text-slate-400 font-medium">Hospital</th>
                    <th className="text-left p-3 text-slate-400 font-medium">Contact</th>
                    <th className="text-left p-3 text-slate-400 font-medium">Status</th>
                    <th className="text-left p-3 text-slate-400 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {registrationRequests.map((req) => (
                    <tr key={req.id} className="border-b border-zinc-800/50">
                      <td className="p-3">
                        <p className="text-white">{req.hospital_name}</p>
                        <p className="text-slate-500 text-sm">{req.email}</p>
                      </td>
                      <td className="p-3 text-slate-300">{req.contact_name}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          req.status === "pending" ? "bg-amber-500/20 text-amber-400" :
                          req.status === "approved" ? "bg-green-500/20 text-green-400" :
                          "bg-red-500/20 text-red-400"
                        }`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="p-3 text-slate-400 text-sm">
                        {new Date(req.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {registrationRequests.length === 0 && (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-slate-400">
                        No registration requests yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Hospitals Tab */}
      {activeTab === "hospitals" && (
        <div className="card-clinical overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-4 text-slate-400 font-medium">Hospital</th>
                <th className="text-left p-4 text-slate-400 font-medium">Contact</th>
                <th className="text-left p-4 text-slate-400 font-medium">Email</th>
                <th className="text-left p-4 text-slate-400 font-medium">Password</th>
                <th className="text-left p-4 text-slate-400 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {hospitals.map((hosp) => (
                <tr key={hosp.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="p-4">
                    <p className="text-white font-medium">{hosp.hospital_name}</p>
                    {hosp.address && <p className="text-slate-500 text-sm">{hosp.address}</p>}
                  </td>
                  <td className="p-4 text-slate-300">{hosp.contact_name}</td>
                  <td className="p-4 text-slate-300">{hosp.email}</td>
                  <td className="p-4">
                    <span className="text-slate-500 italic text-sm">••••••••</span>
                  </td>
                  <td className="p-4 text-slate-400 text-sm">
                    {new Date(hosp.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {hospitals.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-slate-400">
                    No hospital accounts yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === "requests" && (
        <>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard title="Total Requests" value={requests.length} icon={Package} color="blue" />
        <StatsCard title="Pending" value={requests.filter(r => r.status === "submitted").length} icon={Clock} color="amber" />
        <StatsCard title="In Progress" value={requests.filter(r => ["sent_to_suppliers", "priced", "ordered"].includes(r.status)).length} icon={Truck} color="purple" />
        <StatsCard title="Suppliers" value={suppliers.length} icon={Building2} color="green" />
      </div>

      {/* Filter + View Mode Toggle */}
      <div className="flex items-center justify-between gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 bg-white border-slate-200 text-slate-700 hover:border-slate-300">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(BACKORDER_STATUS_CONFIG).map(([key, val]) => (
              <SelectItem key={key} value={key}>{val.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* View Mode Toggle - matching Hospital design */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 border border-slate-200">
          <button
            onClick={() => setViewMode("list")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === "list" 
                ? "bg-blue-600 text-white shadow-sm" 
                : "text-slate-600 hover:bg-slate-200"
            }`}
            title="Table View"
            data-testid="view-mode-list"
          >
            <List className="w-4 h-4" />
            <span>Table</span>
          </button>
          <button
            onClick={() => setViewMode("pipeline")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === "pipeline" 
                ? "bg-blue-600 text-white shadow-sm" 
                : "text-slate-600 hover:bg-slate-200"
            }`}
            title="Pipeline View"
            data-testid="view-mode-pipeline"
          >
            <LayoutGrid className="w-4 h-4" />
            <span>Pipeline</span>
          </button>
        </div>
      </div>

      {/* Table View */}
      {viewMode === "list" && (
      <div className="card-clinical overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left p-4 text-slate-700 font-semibold">ID</th>
              <th className="text-left p-4 text-slate-700 font-semibold">Hospital</th>
              <th className="text-left p-4 text-slate-700 font-semibold">Items</th>
              <th className="text-left p-4 text-slate-700 font-semibold">Status</th>
              <th className="text-left p-4 text-slate-700 font-semibold">Total</th>
              <th className="text-left p-4 text-slate-700 font-semibold">Date</th>
              <th className="text-right p-4 text-slate-700 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => {
              const statusConfig = BACKORDER_STATUS_CONFIG[req.status] || BACKORDER_STATUS_CONFIG.submitted;
              return (
                <tr key={req.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => setSelectedRequest(req)}>
                  <td className="p-4 font-mono text-slate-600">#{req.id.slice(0, 8).toUpperCase()}</td>
                  <td className="p-4">
                    <p className="text-slate-900 font-medium">{req.hospital_name}</p>
                    <p className="text-slate-500 text-sm">{req.contact_email}</p>
                  </td>
                  <td className="p-4 text-slate-600">{req.items?.length || 0} items</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                      {statusConfig.label}
                    </span>
                  </td>
                  <td className="p-4 text-emerald-600 font-medium">
                    {req.total_cost ? `$${req.total_cost.toLocaleString()}` : "-"}
                  </td>
                  <td className="p-4 text-slate-500 text-sm">
                    {new Date(req.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-right">
                    <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900 hover:bg-slate-100" onClick={(e) => { e.stopPropagation(); setSelectedRequest(req); }}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
            {filteredRequests.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-slate-400">
                  No backorder requests found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      
      {/* Pipeline View */}
      {viewMode === "pipeline" && (
        <div className="card-clinical p-6">
          <BackorderPipeline 
            viewMode="admin"
            onRefresh={fetchData}
          />
        </div>
      )}
        </>
      )}

      {/* Add Supplier Modal */}
      <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Add New Supplier</DialogTitle>
            <DialogDescription>Create a new supplier for quote requests</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Supplier Name *</Label>
              <Input 
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={newSupplier.email}
                onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input 
                value={newSupplier.contact_name}
                onChange={(e) => setNewSupplier({ ...newSupplier, contact_name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={newSupplier.phone}
                onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSupplierModal(false)}>Cancel</Button>
            <Button onClick={handleCreateSupplier}>Create Supplier</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Hospital Modal */}
      <Dialog open={showHospitalModal} onOpenChange={setShowHospitalModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Add Hospital Account</DialogTitle>
            <DialogDescription>Create a hospital account for backorder access</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Hospital Name *</Label>
              <Input 
                value={newHospital.hospital_name}
                onChange={(e) => setNewHospital({ ...newHospital, hospital_name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Contact Name</Label>
              <Input 
                value={newHospital.contact_name}
                onChange={(e) => setNewHospital({ ...newHospital, contact_name: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                type="email"
                value={newHospital.email}
                onChange={(e) => setNewHospital({ ...newHospital, email: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input 
                value={newHospital.phone}
                onChange={(e) => setNewHospital({ ...newHospital, phone: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHospitalModal(false)}>Cancel</Button>
            <Button onClick={handleCreateHospital}>Create Hospital</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Proxy Backorder Modal */}
      <Dialog open={showProxyBackorderModal} onOpenChange={setShowProxyBackorderModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="proxy-backorder-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Package className="w-5 h-5 text-blue-600" />
              Create Backorder for Hospital
            </DialogTitle>
            <DialogDescription>
              Create a backorder request on behalf of a hospital. Suppliers and the hospital will be notified automatically.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Hospital Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Select Hospital *</Label>
              <Select
                value={proxyBackorderData.hospital_id}
                onValueChange={(value) => {
                  const hospital = hospitals.find(h => h.id === value);
                  setProxyBackorderData(prev => ({
                    ...prev,
                    hospital_id: value,
                    health_system_name: hospital?.hospital_name || hospital?.name || ""
                  }));
                }}
              >
                <SelectTrigger data-testid="proxy-hospital-select">
                  <SelectValue placeholder="Choose a hospital..." />
                </SelectTrigger>
                <SelectContent>
                  {hospitals.map(h => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.hospital_name || h.name} - {h.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Urgency */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Urgency Level</Label>
              <Select
                value={proxyBackorderData.urgency}
                onValueChange={(value) => setProxyBackorderData(prev => ({ ...prev, urgency: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Standard processing</SelectItem>
                  <SelectItem value="medium">Medium - Priority handling</SelectItem>
                  <SelectItem value="high">High - Urgent response needed</SelectItem>
                  <SelectItem value="critical">Critical - Immediate attention required</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Items Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Items *</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addProxyItem}
                  className="h-7 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              
              {proxyBackorderData.items.map((item, idx) => (
                <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Item {idx + 1}</span>
                    {proxyBackorderData.items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeProxyItem(idx)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Item Name *</Label>
                      <Input
                        value={item.item_name}
                        onChange={(e) => updateProxyItem(idx, "item_name", e.target.value)}
                        placeholder="e.g., Surgical Gloves"
                        className="mt-1"
                        data-testid={`proxy-item-name-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">MFR/Part Number</Label>
                      <Input
                        value={item.mfr_number}
                        onChange={(e) => updateProxyItem(idx, "mfr_number", e.target.value)}
                        placeholder="e.g., MFR-12345"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Quantity</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateProxyItem(idx, "quantity", parseInt(e.target.value) || 1)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit Type</Label>
                      <Select
                        value={item.quantity_type}
                        onValueChange={(value) => updateProxyItem(idx, "quantity_type", value)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="units">Units</SelectItem>
                          <SelectItem value="boxes">Boxes</SelectItem>
                          <SelectItem value="cases">Cases</SelectItem>
                          <SelectItem value="pallets">Pallets</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Item Notes</Label>
                    <Textarea
                      value={item.notes}
                      onChange={(e) => updateProxyItem(idx, "notes", e.target.value)}
                      placeholder="Any specific requirements for this item..."
                      className="mt-1 h-16"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* General Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Admin Notes</Label>
              <Textarea
                value={proxyBackorderData.notes}
                onChange={(e) => setProxyBackorderData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Internal notes about this proxy request..."
                className="h-20"
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Notification Flow
              </h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• Suppliers will receive email + Telegram push notifications to bid</li>
                <li>• Hospital will receive confirmation that Admin initiated this request</li>
                <li>• All supplier replies will sync to both Hospital and Admin portals</li>
                <li>• Hospital retains primary party status for 2-way communication</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowProxyBackorderModal(false)}
              disabled={creatingProxyBackorder}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateProxyBackorder}
              disabled={creatingProxyBackorder || !proxyBackorderData.hospital_id || !proxyBackorderData.items[0].item_name}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="submit-proxy-backorder-btn"
            >
              {creatingProxyBackorder ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Create & Notify
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slide-out Detail Panel */}
      {selectedRequest && (
        <BackorderDetailPanel
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onStatusChange={handleUpdateStatus}
          onSendToSuppliers={handleSendToSuppliers}
          onFinalizePricing={handleFinalizePricing}
          onApproveQuote={handleApproveQuote}
          onDeclineQuote={handleDeclineQuote}
          onDownloadQuotePdf={handleDownloadQuotePdf}
          onEmailQuotePdf={handleEmailQuotePdf}
          token={token}
        />
      )}

      {/* Tracking Information Modal */}
      <Dialog open={showTrackingModal} onOpenChange={setShowTrackingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Enter Shipping Information
            </DialogTitle>
            <DialogDescription>
              Add tracking details for this shipment. This information will be sent to the hospital.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="carrier">Carrier *</Label>
              <Select 
                value={trackingInfo.carrier} 
                onValueChange={(v) => setTrackingInfo({...trackingInfo, carrier: v})}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select carrier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UPS">UPS</SelectItem>
                  <SelectItem value="FedEx">FedEx</SelectItem>
                  <SelectItem value="USPS">USPS</SelectItem>
                  <SelectItem value="DHL">DHL</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tracking_number">Tracking Number *</Label>
              <Input
                id="tracking_number"
                placeholder="Enter tracking number"
                value={trackingInfo.tracking_number}
                onChange={(e) => setTrackingInfo({...trackingInfo, tracking_number: e.target.value})}
                className="font-mono"
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700">
                <strong>📧 Notification:</strong> The hospital will receive an email with tracking information and a link to track the package.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowTrackingModal(false);
                setPendingStatusChange(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleTrackingSubmit}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Mark as Shipped
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
